import React, { useState, useEffect, useCallback } from 'react';
import { trackGuestGrade, trackGuestCapHit } from '../utils/guestEvents';
import GradeCard from './GradeCard';
import { trackGrade } from '../utils/xpixel';
import './HeroGradeWidget.css';

// Interactive "grade any ticker, free" hero widget. Calls the public, no-auth
// endpoint (/api/public/grade/{ticker}) so a cold visitor experiences value
// BEFORE the signup gate. The grade display (expandable 34-test breakdown) is
// shared via GradeCard. Capped at FREE_LIMIT user-initiated grades, then a
// signup gate. Pre-loads a popular ticker (doesn't count toward the cap).
const DEFAULT_TICKER = 'NVDA';
const FREE_LIMIT = 5;
const USE_KEY = 'ag_free_grades';

const refundGrade = () => {
  try { localStorage.setItem(USE_KEY, String(Math.max(0, readCount() - 1))); } catch { /* ignore */ }
};

const readCount = () => {
  const n = parseInt(localStorage.getItem(USE_KEY) || '0', 10);
  return Number.isFinite(n) ? n : 0;
};

// Redacted lines: suggest that prose lives here without inventing sentences
// that could read as real analysis of the ticker. (A ChartMock used to sit
// alongside this for the locked price-vs-fundamentals panel. That panel is
// gone: the chart data was public all along, so the lock hid our best visual
// from the exact people we pay to bring here while protecting nothing.)
function RedactedLines({ widths }) {
  return (
    <div className="hgw-lock-art hgw-lock-lines" aria-hidden="true">
      {widths.map((w, i) => <span key={i} style={{ width: `${w}%` }} />)}
    </div>
  );
}

function LockedPanel({ title, blurb, art, onGetStarted }) {
  return (
    <button className="hgw-lock" onClick={onGetStarted}>
      <div className="hgw-lock-head">
        <span className="hgw-lock-title">{title}</span>
        <span className="hgw-lock-pill">🔒 Free account</span>
      </div>
      <div className="hgw-lock-preview">
        {art}
        <span className="hgw-lock-veil" />
      </div>
      <div className="hgw-lock-blurb">{blurb}</div>
    </button>
  );
}

export default function HeroGradeWidget({ onGetStarted, variant }) {
  const [input, setInput] = useState('');
  const [grade, setGrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gated, setGated] = useState(false);

  const runGrade = useCallback(async (ticker, { counts }) => {
    const t = (ticker || '').trim().toUpperCase();
    if (!t) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/public/grade/${encodeURIComponent(t)}`);
      if (!res.ok) {
        if (counts) refundGrade();
        setError(res.status === 404
          ? `We couldn’t grade ${t} — try another ticker.`
          : 'Something went wrong. Try again in a moment.');
      } else {
        const data = await res.json();
        setGrade(data);
        setError('');
        if (counts) {
          // Counted at submit (below); this is only the conversion signal.
          // Mid-funnel conversion signal for X's optimizer (inert until wired up).
          // Only user-initiated grades count — the preloaded default doesn't fire.
          trackGrade();
        }
      }
    } catch {
      if (counts) refundGrade();
      setError('Something went wrong. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Pre-load a popular ticker (doesn't consume a free grade).
  useEffect(() => { runGrade(DEFAULT_TICKER, { counts: false }); }, [runGrade]);

  const submit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    if (readCount() >= FREE_LIMIT) {
      // Reaching the cap is the interesting event: this visitor wanted a sixth.
      trackGuestCapHit('hero');
      setGated(true);
      return;
    }
    // Count at SUBMIT, not on completion: submitting again while a slow grade
    // was still loading slipped past the cap (a 6th free grade, 2026-11-02).
    // A failed grade gives it back (refundGrade).
    localStorage.setItem(USE_KEY, String(readCount() + 1));
    trackGuestGrade(input.trim().toUpperCase(), 'hero');
    runGrade(input, { counts: true });
    setInput('');
  };

  const remaining = Math.max(0, FREE_LIMIT - readCount());

  const cls = `hgw${variant === 'panel' ? ' hgw-panel' : ''}`;

  if (gated) {
    return (
      <div className={cls}>
        <div className="hgw-gate">
          <div className="hgw-gate-icon">🔓</div>
          <h3 className="hgw-gate-title">You’ve used your {FREE_LIMIT} free grades</h3>
          <p className="hgw-gate-text">
            Sign up free to keep grading any stock — plus ask <b>Alpha</b> about it
            and see how it fits <b>your</b> portfolio.
          </p>
          <button className="hgw-cta" onClick={onGetStarted}>Create your free account →</button>
          <div className="hgw-gate-note">No credit card required.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cls}>
      <div className="hgw-kicker">Try it now — free, no account</div>
      <form className="hgw-search" onSubmit={submit}>
        <input
          className="hgw-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Grade any stock — AAPL, TSLA, COIN…"
          aria-label="Enter a ticker to grade"
          maxLength={8}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck="false"
        />
        <button className="hgw-btn" type="submit" disabled={loading}>
          {loading ? 'Grading…' : 'Grade it'}
        </button>
      </form>

      {error && <div className="hgw-error">{error}</div>}

      {grade && !error && (
        <>
          <GradeCard grade={grade} showNews={false} />

          {/* Everything above is the complete grade — all 34 tests, the DCF, the
              raw inputs. What's locked below is the account-only layer: the AI
              analyst, the deep-dive chart, and anything that needs to know what
              the visitor actually owns. Locks sit UNDER a working result on
              purpose; a cold visitor who meets a wall first just leaves. */}
          <div className="hgw-locks">
            <div className="hgw-locks-head">
              Also included, free with an account
            </div>

            <LockedPanel
              title={`Ask Alpha about ${grade.ticker}`}
              blurb={`Put the score in plain English: why ${grade.ticker} lands where it does, what would have to change, and what the risk actually is.`}
              art={<RedactedLines widths={[96, 88, 93, 61]} />}
              onGetStarted={onGetStarted}
            />

            <LockedPanel
              title="How it fits your portfolio"
              blurb={`Add ${grade.ticker} to a portfolio and see what it does to your concentration and your sector tilt, with every position you hold graded the same way.`}
              art={<RedactedLines widths={[74, 90, 55]} />}
              onGetStarted={onGetStarted}
            />
          </div>

          <button className="hgw-cta" onClick={onGetStarted}>
            Unlock both — free, no card →
          </button>
          <div className="hgw-remaining">
            {remaining > 0
              ? `${remaining} free grade${remaining === 1 ? '' : 's'} left without an account`
              : 'Sign up to keep grading'}
          </div>
        </>
      )}

      {!grade && loading && (
        <div className="hgw-result hgw-skeleton">
          <div className="hgw-skel-line" style={{ width: '55%' }} />
          <div className="hgw-skel-line" style={{ width: '35%', height: 34 }} />
          <div className="hgw-skel-line" style={{ width: '100%' }} />
          <div className="hgw-skel-line" style={{ width: '100%' }} />
        </div>
      )}
    </div>
  );
}
